import { useEffect, useMemo, useState } from 'react';

import { ApiError, api } from '../../api/client';
import { Banner, Card, Empty } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';
import { useAuth } from '../../hooks/useAuth';

const key = (a: number, b: number) => (a < b ? `${a}-${b}` : `${b}-${a}`);

/**
 * Combinaisons de rôles tenables en même temps (brief §3).
 *
 * Réglage d'organisation, distinct des préférences de chaque personne : on
 * décide ici de ce qui est **possible** (on ne joue pas de la batterie en
 * chantant), et sur la fiche de chacun de ce qui est **préféré** parmi les
 * combinaisons possibles.
 *
 * Convention retenue : ce qui n'est pas coché est interdit. Tenir deux rôles à
 * la fois est l'exception, elle se déclare.
 */
export default function RolesScreen() {
  const { isAdmin } = useAuth();
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const roles = useAsync(() => api.roles(), []);
  const teams = useAsync(() => api.teams(), []);
  const compatibility = useAsync(() => api.roleCompatibility(), []);

  useEffect(() => {
    if (compatibility.data) {
      setChecked(new Set(compatibility.data.map((p) => key(p.role_a_id, p.role_b_id))));
    }
  }, [compatibility.data]);

  const pairs = useMemo(() => {
    const list = roles.data ?? [];
    const out: { a: number; b: number }[] = [];
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        out.push({ a: list[i]!.id, b: list[j]!.id });
      }
    }
    return out;
  }, [roles.data]);

  const roleName = (id: number) =>
    (roles.data ?? []).find((role) => role.id === id)?.name ?? `rôle ${id}`;
  const teamName = (id: number) =>
    (teams.data ?? []).find((team) => team.id === id)?.name ?? '';
  const teamOf = (id: number) => (roles.data ?? []).find((role) => role.id === id)?.team_id;

  if (!isAdmin) {
    return (
      <Banner kind="info">
        Les combinaisons de rôles sont réglées par un administrateur. Vous pouvez en
        revanche indiquer, sur la fiche de chaque personne, celles qu'elle préfère.
      </Banner>
    );
  }

  const save = async () => {
    if (checked === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.replaceRoleCompatibility(
        [...checked].map((entry) => {
          const [a, b] = entry.split('-').map(Number);
          return { role_a_id: a!, role_b_id: b!, note: null };
        }),
      );
      compatibility.reload();
      setNotice('Combinaisons enregistrées.');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.detail : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (a: number, b: number) =>
    setChecked((previous) => {
      const next = new Set(previous ?? []);
      const entry = key(a, b);
      if (next.has(entry)) next.delete(entry);
      else next.add(entry);
      return next;
    });

  return (
    <>
      <h1>Combinaisons de rôles</h1>
      <p className="muted">
        Cochez les paires de rôles qu'une même personne peut tenir au cours du même
        événement. Tout ce qui n'est pas coché est interdit.
      </p>

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="success">{notice}</Banner>}

      <Card
        title="Ce qui est possible"
        actions={
          <button className="primary" onClick={save} disabled={busy || checked === null}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        }
      >
        {pairs.length === 0 ? (
          <Empty>Déclarez d'abord au moins deux rôles.</Empty>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Combinaison</th>
                  <th>Équipes</th>
                  <th>Possible</th>
                </tr>
              </thead>
              <tbody>
                {pairs.map(({ a, b }) => {
                  const entry = key(a, b);
                  const sameTeam = teamOf(a) === teamOf(b);
                  return (
                    <tr key={entry}>
                      <td>
                        {roleName(a)} + {roleName(b)}
                      </td>
                      <td className="muted">
                        {sameTeam
                          ? teamName(teamOf(a) ?? 0)
                          : `${teamName(teamOf(a) ?? 0)} / ${teamName(teamOf(b) ?? 0)}`}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${roleName(a)} + ${roleName(b)}`}
                          checked={checked?.has(entry) ?? false}
                          disabled={busy || checked === null}
                          onChange={() => toggle(a, b)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted" style={{ marginTop: 12 }}>
          Un plafond par personne limite en outre le nombre de rôles tenus en même temps,
          réglable sur sa fiche : sans lui, trois rôles compatibles deux à deux pourraient
          être cumulés tous les trois.
        </p>
      </Card>
    </>
  );
}
